import React, { ReactNode } from 'react';

// Base interface for all trajectory cards
export interface BaseTrajectoryProps {
  currentPoints: number;
  targetPoints: number;
  dailyAverage: number;
  targetDate?: string;
  daysActive: number;
}

// Theme vocabulary interface
export interface ThemeVocabulary {
  unit: string;          // "points" | "bricks" | "energy"
  pace: string;          // "points/day" | "bricks/day" | "energy/day"
  goal: string;          // "Nintendo Switch" | "Nintendo Switch LEGO Set" | "Golden Nintendo Switch"
  completion: string;    // "completed" | "built" | "mastered"
  current: string;       // "Current Pace" | "Building Rate" | "Power Level"
  remaining: string;     // "Remaining" | "Bricks Needed" | "Energy Required"
}

// Status type for trajectory assessment
export type TrajectoryStatus = 'excellent' | 'good' | 'monitor' | 'speedup';

// Abstract base class for trajectory cards
export abstract class BaseTrajectoryCard {
  protected props: BaseTrajectoryProps;
  
  constructor(props: BaseTrajectoryProps) {
    this.props = props;
  }
  
  // Core calculation methods (shared across all themes)
  protected calculateRemaining(): number {
    return this.props.targetPoints - this.props.currentPoints;
  }
  
  protected calculateProgressPercentage(): number {
    return (this.props.currentPoints / this.props.targetPoints) * 100;
  }
  
  protected calculateDaysToComplete(): number {
    const remaining = this.calculateRemaining();
    return Math.ceil(remaining / (this.props.dailyAverage || 1));
  }
  
  protected calculateProjectedDate(): Date {
    const daysToComplete = this.calculateDaysToComplete();
    const projectedDate = new Date();
    projectedDate.setDate(projectedDate.getDate() + daysToComplete);
    return projectedDate;
  }
  
  protected calculateRequiredDailyPace(): number {
    if (!this.props.targetDate) return this.props.dailyAverage;
    
    const target = new Date(this.props.targetDate);
    const today = new Date();
    const daysUntilTarget = Math.ceil((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    const remaining = this.calculateRemaining();
    
    return remaining / Math.max(daysUntilTarget, 1);
  }
  
  protected determineStatus(): TrajectoryStatus {
    const required = this.calculateRequiredDailyPace();
    const current = this.props.dailyAverage;
    
    if (current >= required * 1.2) return 'excellent';
    if (current >= required * 1.0) return 'good';
    if (current >= required * 0.8) return 'monitor';
    return 'speedup';
  }
  
  protected formatTimeRemaining(date: Date): string {
    const today = new Date();
    const diffTime = date.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays <= 0) return "TODAY! 🎉";
    if (diffDays === 1) return "TOMORROW! 🚀";
    if (diffDays <= 7) return `${diffDays} days (${date.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })})`;
    if (diffDays <= 30) return `${diffDays} days (${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })})`;
    
    const weeks = Math.floor(diffDays / 7);
    const remainingDays = diffDays % 7;
    if (weeks === 1) {
      return `1 week${remainingDays > 0 ? ` & ${remainingDays} days` : ''} (${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })})`;
    }
    return `${weeks} weeks${remainingDays > 0 ? ` & ${remainingDays} days` : ''} (${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })})`;
  }
  
  protected getStatusMessage(): { title: string; description: string; suggestions: string[] } {
    const status = this.determineStatus();
    const vocabulary = this.getThemeVocabulary();
    const required = this.calculateRequiredDailyPace();
    
    switch (status) {
      case 'excellent':
        return {
          title: `Excellent ${vocabulary.pace.split('/')[0]} rate! 🏆`,
          description: `Keep earning ${this.props.dailyAverage.toFixed(0)}+ ${vocabulary.unit} daily and you'll reach your ${vocabulary.goal} right on schedule!`,
          suggestions: []
        };
      
      case 'good':
        return {
          title: `Great momentum! 🎯`,
          description: `You're on track! Continue at ${this.props.dailyAverage.toFixed(0)} ${vocabulary.unit} per day.`,
          suggestions: []
        };
      
      case 'monitor':
        return {
          title: `Small adjustment needed 📈`,
          description: `Increase to ${Math.ceil(required)} ${vocabulary.unit}/day. You're close - just need a small push!`,
          suggestions: ['Extra Sanskrit (15 pts)', 'Math practice (12 pts)', 'Reading session (10 pts)']
        };
      
      case 'speedup':
        return {
          title: `Time to level up! 🚀`,
          description: `Boost to ${Math.ceil(required)} ${vocabulary.unit}/day to stay on track. Try adding one more activity!`,
          suggestions: ['Sanskrit +15', 'Math practice +12', 'Reading +10']
        };
    }
  }
  
  // Abstract methods that must be implemented by child classes
  abstract getThemeVocabulary(): ThemeVocabulary;
  abstract renderHeader(): ReactNode;
  abstract renderPredictionSection(): ReactNode;
  abstract renderProgressVisualization(): ReactNode;
  abstract renderMetrics(): ReactNode;
  abstract getContainerClassName(): string;
  
  // Main render method that orchestrates the layout
  render(): ReactNode {
    return (
      <div className={this.getContainerClassName()}>
        {this.renderHeader()}
        {this.renderPredictionSection()}
        {this.renderProgressVisualization()}
        {this.renderMetrics()}
      </div>
    );
  }
}

// React component wrapper for the base trajectory card
export interface BaseTrajectoryCardProps extends BaseTrajectoryProps {
  implementation: BaseTrajectoryCard;
}

export const TrajectoryCardRenderer: React.FC<BaseTrajectoryCardProps> = ({ implementation }) => {
  return <>{implementation.render()}</>;
};

export default BaseTrajectoryCard;